import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState
} from '@xyflow/react';
import type { Department, User } from '@bliks-io/contracts';
import { DepartmentNode } from './DepartmentNode';
import { OrganizationNode } from './OrganizationNode';
import {
  buildOrganigramLayout,
  type DepartmentNodeData,
  type OrgInfo
} from '../utils/buildOrganigramLayout';

const nodeTypes = {
  departmentNode: DepartmentNode,
  organizationNode: OrganizationNode
};

export interface OrganigramCanvasProps {
  departments: Department[];
  orgMembers?: User[];
  organization?: OrgInfo;
  direction?: 'TB' | 'LR';
  /** Expand/collapse, selection, pan and zoom — disable for embedded previews. */
  interactive?: boolean;
  /** Zoom controls and minimap — hide for embedded previews. */
  showControls?: boolean;
}

/**
 * React Flow canvas rendering the department hierarchy. Fills its parent, so
 * consumers must give it a sized container.
 */
export function OrganigramCanvas({
  departments,
  orgMembers,
  organization,
  direction = 'TB',
  interactive = true,
  showControls = true
}: OrganigramCanvasProps) {
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());

  const { initialNodes, initialEdges } = useMemo(() => {
    if (departments.length === 0) {
      return { initialNodes: [], initialEdges: [] };
    }

    const { nodes, edges } = buildOrganigramLayout({
      departments,
      direction,
      orgMembers,
      expandedDepartments,
      organization
    });
    return { initialNodes: nodes, initialEdges: edges };
  }, [departments, direction, orgMembers, expandedDepartments, organization]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const handleToggleExpand = useCallback((departmentId: string) => {
    setExpandedDepartments((prev) => {
      const next = new Set(prev);
      if (next.has(departmentId)) {
        next.delete(departmentId);
      } else {
        next.add(departmentId);
      }
      return next;
    });
  }, []);

  useEffect(
    function syncLayoutIntoFlowState() {
      if (initialNodes.length > 0) {
        // Non-interactive previews omit the toggle handler, so their nodes stay collapsed
        const nodesToSet = interactive
          ? initialNodes.map((node) => ({
              ...node,
              data: {
                ...node.data,
                onToggleExpand: handleToggleExpand
              }
            }))
          : initialNodes;
        setNodes(nodesToSet);
        setEdges(initialEdges);
      }
    },
    [initialNodes, initialEdges, setNodes, setEdges, handleToggleExpand, interactive]
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      fitView
      fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
      minZoom={0.1}
      maxZoom={1.5}
      defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
      proOptions={{ hideAttribution: true }}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={interactive}
      panOnDrag={interactive}
      zoomOnScroll={interactive}
      zoomOnPinch={interactive}
      zoomOnDoubleClick={interactive}
      preventScrolling={interactive}
    >
      <Background color="#f5f5f5" gap={20} size={0.5} />
      {showControls && <Controls position="bottom-right" showInteractive={false} />}
      {showControls && (
        <MiniMap
          position="bottom-left"
          nodeColor={(node) => {
            const data = node.data as DepartmentNodeData;
            const level = data?.level ?? 1;
            if (level === 0) return '#6b7280'; // gray-500 (organization root)
            if (level === 1) return '#3b82f6'; // blue-500
            if (level === 2) return '#60a5fa'; // blue-400
            if (level === 3) return '#93c5fd'; // blue-300
            return '#bfdbfe'; // blue-200
          }}
          maskColor="rgba(0, 0, 0, 0.1)"
        />
      )}
    </ReactFlow>
  );
}
